import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";
import { useRoleManager } from "context/RoleManagerProvider";
import { useFetchStateHelper, useMergedFetchState } from "hooks/useFetchState";

const INVOICE_POLL_INTERVAL_MS = 2000;
const INVOICE_WAIT_MS = 30000;

// The invoice is issued by a queued Celery task after the subscription is
// activated, and latestInvoiceId is the farmer's newest invoice of any kind —
// so the invoice is only this payment's once the id differs from the one the
// row had before paying.
const hasNewInvoice = (farmer, previousInvoiceId) =>
  Boolean(farmer.latestInvoiceId) && farmer.latestInvoiceId !== previousInvoiceId;

export const useKydPaymentActions = () => {
  const queryClient = useQueryClient();
  const { currentTeam } = useRoleManager();
  const teamId = currentTeam?.id;

  const { mutateAsync: checkout, ...checkoutResponse } = useMutation({
    mutationKey: ["kyd-payment-checkout"],
    mutationFn: ({ userId, tier }) => api.payments.kydCheckout(userId, tier),
  });
  const { fetchState: checkoutFetchState } =
    useFetchStateHelper(checkoutResponse);

  // Cash payments activate the subscription synchronously but the invoice is
  // issued by a queued Celery task, so refreshing the list right away would
  // show the farmer as paid without the invoice icon. Hold the mutation open
  // until the new invoice shows up, then write the list to the cache once so
  // the amount, status and invoice appear together. On timeout the paid row
  // is still applied.
  const { mutateAsync: cashPayment, ...cashResponse } = useMutation({
    mutationKey: ["kyd-payment-cash"],
    mutationFn: ({ userId, tier }) => api.payments.kydCashPayment(userId, tier),
    onSuccess: async (_, { userId }) => {
      const findFarmer = (farmers) => farmers?.find((row) => row.id === userId);
      const previousInvoiceId = findFarmer(
        queryClient.getQueryData(["my-farmers", teamId])
      )?.latestInvoiceId;

      const deadline = Date.now() + INVOICE_WAIT_MS;
      let farmers = await api.users.fetchMyFarmers(teamId);
      while (
        !hasNewInvoice(findFarmer(farmers), previousInvoiceId) &&
        Date.now() < deadline
      ) {
        await new Promise((resolve) =>
          setTimeout(resolve, INVOICE_POLL_INTERVAL_MS)
        );
        farmers = await api.users.fetchMyFarmers(teamId);
      }
      queryClient.setQueryData(["my-farmers", teamId], farmers);
    },
  });
  const { fetchState: cashFetchState } = useFetchStateHelper(cashResponse);

  // The code exists as soon as the POST returns (no invoice until it is paid),
  // so there is no race — refresh the list so the row shows "RF pending" and
  // the modal switches to the pending view. The returned promise keeps the
  // mutation loading until the row has updated.
  const { mutateAsync: createRfCode, ...rfResponse } = useMutation({
    mutationKey: ["kyd-payment-rf-code"],
    mutationFn: ({ userId, sendSms, description, phone }) =>
      api.payments.kydRfCode(userId, { sendSms, description, phone }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-farmers"] }),
  });
  const { fetchState: rfFetchState } = useFetchStateHelper(rfResponse);

  const { mutateAsync: resendRfSms, ...resendResponse } = useMutation({
    mutationKey: ["kyd-payment-rf-resend-sms"],
    mutationFn: ({ userId, phone }) =>
      api.payments.kydResendRfSms(userId, phone),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-farmers"] }),
  });
  const { fetchState: resendFetchState } = useFetchStateHelper(resendResponse);

  const { mutateAsync: cancelRfCode, ...cancelResponse } = useMutation({
    mutationKey: ["kyd-payment-rf-cancel"],
    mutationFn: (rfCodeId) => api.payments.kydCancelRfCode(rfCodeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-farmers"] }),
  });
  const { fetchState: cancelFetchState } = useFetchStateHelper(cancelResponse);

  const { fetchState } = useMergedFetchState([
    checkoutFetchState,
    cashFetchState,
    rfFetchState,
    resendFetchState,
    cancelFetchState,
  ]);

  return {
    checkout,
    cashPayment,
    createRfCode,
    resendRfSms,
    cancelRfCode,
    checkoutFetchState,
    cashFetchState,
    rfFetchState,
    resendFetchState,
    cancelFetchState,
    fetchState,
  };
};

// The farmer's row from the shared ["my-farmers"] cache. The payment modal is
// opened with a snapshot of the row, which goes stale once a code is created,
// resent or cancelled while it's open; staleTime: Infinity keeps this observer
// from refetching the whole list just because the modal mounted. It polls only
// while the code's SMS is pending so it can move to "sent"/"failed" without a
// reload — there's no push channel from the SMS-sending Celery task.
export const useKydFarmer = (farmerId) => {
  const { currentTeam } = useRoleManager();
  const teamId = currentTeam?.id;
  const { data: farmers } = useQuery({
    queryKey: ["my-farmers", teamId],
    queryFn: () => api.users.fetchMyFarmers(teamId),
    staleTime: Infinity,
    refetchInterval: (query) =>
      query.state.data?.find((row) => row.id === farmerId)?.pendingRfCode
        ?.smsStatus === "pending"
        ? 3000
        : false,
  });

  return farmers?.find((row) => row.id === farmerId);
};

// The logged-in farmer's own RF code awaiting payment, issued by any KYD.
export const useMyPendingRfCode = () => {
  const queryResponse = useQuery({
    queryKey: ["my-pending-rf-code"],
    queryFn: api.payments.fetchMyPendingRfCode,
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  return { pendingRfCode: queryResponse.data ?? null, fetchState };
};

// The requesting user's own current active subscription — null (not an
// error) when they don't have one, since GET /my-subscription/ 404s in that
// case and paymentsApi.fetchMySubscription already resolves that to null.
// shouldPoll re-fetches every 2s (e.g. while a checkout tab is open) so a
// freshly-activated subscription is picked up without a manual reload.
export const useMySubscription = (shouldPoll = false) => {
  const queryResponse = useQuery({
    queryKey: ["my-subscription"],
    queryFn: api.payments.fetchMySubscription,
    refetchInterval: shouldPoll ? 2000 : false,
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  return { subscription: queryResponse.data, fetchState };
};

// The plans the acting role can pick from, as the backend lists them.
export const usePlans = () => {
  const queryResponse = useQuery({
    queryKey: ["plans"],
    queryFn: api.payments.fetchPlans,
  });
  const { fetchState } = useFetchStateHelper(queryResponse);

  return { plans: queryResponse.data, fetchState };
};

// Starts a self-checkout for one of the plans listed by usePlans — the price
// is always recomputed server-side from the user's own land.
export const useSelfCheckoutActions = () => {
  const { mutateAsync: checkout, ...checkoutResponse } = useMutation({
    mutationKey: ["self-checkout"],
    mutationFn: (planId) => api.payments.checkout(planId),
  });
  const { fetchState: checkoutFetchState } =
    useFetchStateHelper(checkoutResponse);

  return { checkout, checkoutFetchState };
};

// Polls until the given farmer is paid *and* this payment's invoice exists
// (or INVOICE_WAIT_MS has passed since it showed as paid, so a stalled invoice
// worker can't hang it), then writes the list to the ["my-farmers"] cache
// once. It polls under its own key so the dashboard row never shows the
// intermediate "paid, no invoice yet" state. previousInvoiceId is the row's
// latestInvoiceId from before the payment started.
export const useFarmerPaymentStatus = (farmerId, previousInvoiceId) => {
  const queryClient = useQueryClient();
  const { currentTeam } = useRoleManager();
  const teamId = currentTeam?.id;
  const paidSinceRef = useRef(null);
  // The row's latestInvoiceId changes once we write the cache below, so the
  // "before" value has to be frozen when polling starts.
  const baselineInvoiceIdRef = useRef(undefined);
  if (farmerId && baselineInvoiceIdRef.current === undefined) {
    baselineInvoiceIdRef.current = previousInvoiceId;
  }

  const isSettled = (farmers) => {
    const current = farmers?.find((row) => row.id === farmerId);
    if (!current?.hasPaidSubscription) return false;
    return (
      hasNewInvoice(current, baselineInvoiceIdRef.current) ||
      Date.now() - paidSinceRef.current > INVOICE_WAIT_MS
    );
  };

  const { data: farmers } = useQuery({
    queryKey: ["farmer-payment-status", farmerId, teamId],
    queryFn: async () => {
      const data = await api.users.fetchMyFarmers(teamId);
      if (data.find((row) => row.id === farmerId).hasPaidSubscription) {
        paidSinceRef.current ??= Date.now();
      }
      if (isSettled(data)) queryClient.setQueryData(["my-farmers", teamId], data);
      return data;
    },
    enabled: Boolean(farmerId),
    gcTime: 0,
    refetchInterval: (query) =>
      isSettled(query.state.data) ? false : INVOICE_POLL_INTERVAL_MS,
  });

  return { isPaid: isSettled(farmers) };
};
